import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserGroupsOptimized } from '@/hooks/useUserGroupsOptimized';
import { ChallengeMatch, ChallengeSuggestion } from '@/types/challengeMatch';
import { PairLadderWithMovement } from '@/types/pairsLadder';
import { computeChallengeSuggestions } from '@/utils/challengeSuggestionLogic';

const DEFAULTS = {
  challenge_range: 3,
  points_multiplier: 1.5,
  challenge_expiry_hours: 48,
  auto_suggest_enabled: true,
};

const PAIR_SELECT = `
  id, ladder_rank, ladder_points, matches_played, wins, losses, player1_id, player2_id,
  player1:profiles!group_pair_ladders_player1_id_fkey(id, full_name, avatar_url),
  player2:profiles!group_pair_ladders_player2_id_fkey(id, full_name, avatar_url)
`;

export interface GroupChallengeGroup {
  groupId: string;
  groupName: string;
  pointsMultiplier: number;
  expiryHours: number;
  suggestions: ChallengeSuggestion[];
  pendingForMe: ChallengeMatch[];
}

export const MY_CHALLENGES_KEY = (userId?: string | null) => ['home-challenge-suggestions', userId];

/** Stable per-group offset so groups don't rotate in lockstep */
function hashGroupId(groupId: string): number {
  let h = 0;
  for (let i = 0; i < groupId.length; i++) h = (h * 31 + groupId.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * Picks one suggestion per group, advancing once per UTC day so the same
 * suggestion is shown all day and everyone eventually gets a turn.
 */
function pickDailySuggestion(
  suggestions: ChallengeSuggestion[],
  groupId: string
): ChallengeSuggestion[] {
  if (suggestions.length <= 1) return suggestions;
  const dayIndex = Math.floor(Date.now() / 86400000) + hashGroupId(groupId);
  return [suggestions[dayIndex % suggestions.length]];
}


/**
 * Cross-group pair-ladder challenge data for the home screen: challenges
 * awaiting the current user's response plus suggested challenges per group.
 */
export function useMyChallengeSuggestions() {
  const { user } = useAuth();
  const { groups, isLoading: groupsLoading } = useUserGroupsOptimized(user?.id);

  const groupIds = (groups || []).filter(g => g.isMember).map(g => g.id);
  const groupNames = new Map((groups || []).map(g => [g.id, g.name]));

  const query = useQuery({
    queryKey: [...MY_CHALLENGES_KEY(user?.id), groupIds.join(',')],
    enabled: !!user && groupIds.length > 0,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<GroupChallengeGroup[]> => {
      const [laddersRes, challengesRes, settingsRes] = await Promise.all([
        supabase
          .from('group_pair_ladders')
          .select(`
            *,
            player1:profiles!group_pair_ladders_player1_id_fkey(id, full_name, avatar_url, skill_rating),
            player2:profiles!group_pair_ladders_player2_id_fkey(id, full_name, avatar_url, skill_rating)
          `)
          .in('group_id', groupIds)
          .order('ladder_rank', { ascending: true }),
        supabase
          .from('pair_challenge_matches')
          .select(`
            *,
            challenger_pair:group_pair_ladders!pair_challenge_matches_challenger_pair_id_fkey(${PAIR_SELECT}),
            challenged_pair:group_pair_ladders!pair_challenge_matches_challenged_pair_id_fkey(${PAIR_SELECT})
          `)
          .in('group_id', groupIds)
          .in('status', ['pending', 'accepted', 'scheduled'])
          .order('created_at', { ascending: false }),
        supabase
          .from('group_challenge_settings')
          .select('*')
          .in('group_id', groupIds),
      ]);

      if (laddersRes.error) throw laddersRes.error;
      if (challengesRes.error) throw challengesRes.error;
      if (settingsRes.error) throw settingsRes.error;

      const ladders = (laddersRes.data || []) as unknown as (PairLadderWithMovement & { group_id: string })[];
      const challenges = (challengesRes.data || []) as unknown as ChallengeMatch[];
      const settingsByGroup = new Map((settingsRes.data || []).map(s => [s.group_id, s]));

      const result: GroupChallengeGroup[] = [];

      for (const groupId of groupIds) {
        const ladder = ladders.filter(l => l.group_id === groupId);
        if (ladder.length === 0) continue;

        const userPairIds = ladder
          .filter(p => p.player1_id === user!.id || p.player2_id === user!.id)
          .map(p => p.id);
        if (userPairIds.length === 0) continue;

        const groupChallenges = challenges.filter(c => c.group_id === groupId);
        const settings = settingsByGroup.get(groupId);

        const allSuggestions = computeChallengeSuggestions({
          ladder,
          userPairIds,
          challengeRange: settings?.challenge_range ?? DEFAULTS.challenge_range,
          existingChallenges: groupChallenges,
          autoSuggestEnabled: settings?.auto_suggest_enabled ?? DEFAULTS.auto_suggest_enabled,
          limit: 5,
        });

        // Show a single suggestion, rotating daily through the shortlist
        const suggestions = pickDailySuggestion(allSuggestions, groupId);


        // Challenges where the current user still needs to accept/decline
        const pendingForMe = groupChallenges.filter(c => {
          if (c.status !== 'pending') return false;
          const pair = c.challenged_pair;
          if (!pair) return false;
          const isP1 = pair.player1_id === user!.id;
          const isP2 = pair.player2_id === user!.id;
          if (!isP1 && !isP2) return false;
          return isP1 ? !c.challenged_player1_accepted : !c.challenged_player2_accepted;
        });

        if (suggestions.length === 0 && pendingForMe.length === 0) continue;

        result.push({
          groupId,
          groupName: groupNames.get(groupId) || 'Group',
          pointsMultiplier: settings?.points_multiplier ?? DEFAULTS.points_multiplier,
          expiryHours: settings?.challenge_expiry_hours ?? DEFAULTS.challenge_expiry_hours,
          suggestions,
          pendingForMe,
        });
      }

      return result;
    },
  });

  const pendingCount = (query.data || []).reduce((sum, g) => sum + g.pendingForMe.length, 0);

  return {
    groups: query.data || [],
    pendingCount,
    isLoading: groupsLoading || query.isLoading,
  };
}
